import { VAT_PERCENT } from '../constants/pricing'
import type {
  ContractDirection,
  ContractKind,
  ContractStatus,
  DeductionBasis,
  DeductionSettlement,
  InstallmentDueKind,
  InstallmentRole,
  InvoiceStatus,
} from '../enums'
import { FINANCE } from '../locales/de/finance'
import type {
  AllocationFigures,
  ContractFigures,
  InstallmentFigures,
  InstallmentState,
  InvoiceFigures,
  InvoiceState,
  PlannedDeduction,
  ProjectFigures,
  SideFigures,
} from '../models/finance'

import { clamp } from './clamp'

/**
 * The rules a project's finances are computed by, as pure functions over the
 * records. `docs/project-finances.md` §4 states them in prose; the backend's
 * read model and the screens that preview a write both go through here, so a
 * figure is never computed twice in two ways.
 *
 * Every amount is net, EUR, rounded to the cent where it is a result.
 */

// ── The records, as the rules read them ─────────────────────────────────────

export type FinanceDeduction = {
  id: number
  title: string
  /** Exactly one of `percent` and `amount`; a fixed sum is contract-wide. */
  percent: number | null
  amount: number | null
  basis: DeductionBasis
  settlement: DeductionSettlement
  deductAtInstallmentId: number | null
}

export type FinanceInstallment = {
  id: number
  sortOrder: number
  role: InstallmentRole
  percent: number | null
  amount: number | null
  /** A PLANNING_FEE: the installment whose planned amount it reduces. */
  creditedInstallmentId: number | null
  dueKind: InstallmentDueKind
  milestoneId: number | null
  dueDate: Date | string | null
}

export type FinanceApprovalDeduction = {
  deductionId: number | null
  amount: number
  settlement: DeductionSettlement
}

export type FinanceApproval = {
  amount: number
  deductions: FinanceApprovalDeduction[]
}

export type FinanceAllocation = {
  id: number
  sortOrder: number
  installmentId: number | null
  retentionRelease: boolean
  amount: number
  approvals: FinanceApproval[]
}

export type FinancePayment = { amount: number }

export type FinanceInvoice = {
  id: number
  status: InvoiceStatus
  invoiceDate: Date | string
  netAmount: number
  vatRate: number
  dueDate: Date | string | null
  allocations: FinanceAllocation[]
  payments: FinancePayment[]
}

export type FinanceContract = {
  id: number
  direction: ContractDirection
  kind: ContractKind
  status: ContractStatus
  netAmount: number
  installments: FinanceInstallment[]
  deductions: FinanceDeduction[]
  invoices: FinanceInvoice[]
}

// ── Money ───────────────────────────────────────────────────────────────────

/** To the cent. The epsilon keeps 1.005 from rounding down. */
export const roundMoney = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

/** Two amounts that agree to the cent. */
export const sameMoney = (a: number, b: number): boolean =>
  Math.abs(a - b) < 0.005

/**
 * How far a payment may miss what is open and still settle it, EUR: a
 * transfer rounded on the other side's books arrives a cent off, and a
 * gross and its net may lie a cent apart. The difference is shown as such
 * and the invoice counts as paid.
 */
const ROUNDING_TOLERANCE = 0.01

/** Whether two amounts lie within the rounding tolerance of each other. */
export const withinRounding = (a: number, b: number): boolean =>
  Math.abs(roundMoney(a - b)) <= ROUNDING_TOLERANCE

/** Amounts added up, to the cent. */
export const sumMoney = (values: readonly number[]): number =>
  roundMoney(values.reduce((total, value) => total + value, 0))

/** Which way an amount points; a credit note's figures are negative. Zero
 *  counts as positive, so an empty figure is checked like a debit. */
export const signOf = (amount: number): number => Math.sign(amount) || 1

/** The net a gross figure stands for at a rate: what a document printing
 *  only the gross says before VAT. */
export const netOf = (gross: number, vatRate: number): number =>
  roundMoney(gross / (1 + vatRate / 100))

export const grossOf = (net: number, vatRate: number): number =>
  roundMoney(net * (1 + vatRate / 100))

/** Whether a gross lies within rounding of what its net comes to: the two
 *  figures of one payment may be a cent apart. */
export const grossMatchesNet = (
  gross: number,
  net: number,
  vatRate: number,
): boolean => withinRounding(gross, grossOf(net, vatRate))

/** Whether `paid` settles `payable`: all of it, or within rounding of it
 *  once anything was paid at all. */
export const settles = (paid: number, payable: number): boolean =>
  sameMoney(paid, payable) ||
  (!sameMoney(paid, 0) && withinRounding(paid, payable))

/** A percentage of a base amount, to the cent. */
export const percentOf = (base: number, percent: number): number =>
  roundMoney((base * percent) / 100)

/**
 * Whether `amount` runs past `limit` by more than a cent, in the direction
 * `sign` points. A credit note's figures are negative: there, "past" means
 * further below zero.
 */
export const exceeds = (amount: number, limit: number, sign: number): boolean =>
  sign * amount > sign * limit + 0.005

/** Whether an amount points the other way than the invoice it is part of:
 *  a payment, a line or an approval has the invoice's sign, or is nothing. */
export const pointsAgainst = (amount: number, sign: number): boolean =>
  sign * amount <= 0

/** Whether a payment of `amount`, net, can be taken on what is `open` with
 *  `sign`: something is open, the payment points the same way, and it is at
 *  most a rounding over. */
export const paymentFitsOpen = (
  amount: number,
  open: number,
  sign: number,
): boolean =>
  !sameMoney(open, 0) &&
  !pointsAgainst(amount, sign) &&
  !exceeds(amount, roundMoney(open + sign * ROUNDING_TOLERANCE), sign)

// ── Installments ────────────────────────────────────────────────────────────

/** A row of a plan as far as its role goes: every surface's row has it. */
type RoleRow = { role: InstallmentRole }

export const isPlanningFee = (installment: RoleRow): boolean =>
  installment.role === 'PLANNING_FEE'

/** The installments that make up the contract sum, in order; the
 *  Planungspauschale stands beside them. Rows without a `sortOrder` keep
 *  the order they came in. */
export const planInstallments = <T extends RoleRow & { sortOrder?: number }>(
  installments: readonly T[],
): T[] =>
  [...installments]
    .filter(installment => !isPlanningFee(installment))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

/** Where the installment a fee is credited against stands among the plan's
 *  installments, or null where it names none of them. The position is what
 *  a plan without ids — a new contract's, a PDF's — names it by. */
export const creditedInstallmentIndexOf = <
  T extends RoleRow & { id: number; sortOrder?: number },
>(
  installments: readonly T[],
  creditedInstallmentId: number | null,
): number | null => {
  if (creditedInstallmentId == null) return null
  const index = planInstallments(installments).findIndex(
    installment => installment.id === creditedInstallmentId,
  )
  return index === -1 ? null : index
}

/** Whether a contract is the one a party's invoices go on where no
 *  contract covers them: no sum, no plan, no deductions of its own. */
export const isDirect = (contract: { kind: ContractKind }): boolean =>
  contract.kind === 'DIRECT'

/** A VAT rate and a payment term, as a contract or a Firma states them. */
type Terms = { vatRate: number | null; paymentTermDays: number | null }

/**
 * The terms a new DIRECT contract is created with: the Firma's rate and
 * term on the payable side, the customer's main contract's on the
 * receivable one, else the standard rate and no term.
 */
export const directTermsFrom = (
  party: Terms | null | undefined,
  customerMain: Terms | null | undefined,
): { vatRate: number; paymentTermDays: number | null } => ({
  vatRate: party?.vatRate ?? customerMain?.vatRate ?? VAT_PERCENT,
  paymentTermDays:
    party?.paymentTermDays ?? customerMain?.paymentTermDays ?? null,
})

/** The customer's main contract in force, the first written where a project
 *  has several: whose terms the customer's DIRECT contract starts from. */
export const customerMainOf = <
  T extends {
    id: number
    direction: ContractDirection
    kind: ContractKind
    status: ContractStatus
    partyId: number | null
  },
>(
  contracts: readonly T[],
): T | null =>
  contracts
    .filter(
      c =>
        c.direction === 'RECEIVABLE' &&
        c.kind === 'MAIN' &&
        c.status !== 'CANCELLED' &&
        c.partyId == null,
    )
    .sort((a, b) => a.id - b.id)[0] ?? null

/** A Planungspauschale belongs to the customer's main contract: a Gewerk
 *  is not paid one, and a Nachtrag adds to a contract that has it already. */
export const allowsPlanningFee = (contract: {
  direction: ContractDirection
  kind: ContractKind
}): boolean => contract.direction === 'RECEIVABLE' && contract.kind === 'MAIN'

/** Whether a contract may take a Planungspauschale now: one it is allowed
 *  to have, and does not have yet. */
export const canAddPlanningFee = (contract: {
  direction: ContractDirection
  kind: ContractKind
  installments: readonly RoleRow[]
}): boolean =>
  allowsPlanningFee(contract) && !contract.installments.some(isPlanningFee)

/** The Planungspauschale rows of a contract — one in practice. */
export const planningFees = <T extends RoleRow>(
  installments: readonly T[],
): T[] => installments.filter(isPlanningFee)

/** The installments whose milestone is reached and that nobody has billed
 *  yet: the next ones to bill, the Planungspauschale among them. */
export const billableInstallments = <
  T extends { figures: { state: InstallmentState } },
>(
  installments: readonly T[],
): T[] => installments.filter(i => i.figures.state === 'DUE')

/** The approval given last on an invoice, where there is one. */
export const latestApprovalOf = <A extends { approvedAt: string }>(invoice: {
  allocations: readonly { approvals: readonly A[] }[]
}): A | null =>
  invoice.allocations
    .flatMap(a => a.approvals)
    .reduce<A | null>(
      (latest, approval) =>
        latest && latest.approvedAt >= approval.approvedAt ? latest : approval,
      null,
    )

/** A row as the credit sentence reads it. */
type CreditRow = RoleRow & {
  id: number
  title: string
  creditedInstallmentId: number | null
}

/**
 * What a row says about the Planungspauschale: on the fee, which installment
 * it is set off against; on that installment, that it is reduced by the fee.
 * Null on every other row. One sentence for the plan, the customer's view
 * and anything printed from either.
 */
export function installmentCreditNote<T extends CreditRow>(
  row: T,
  rows: readonly T[],
  /** The fee as the surface writes it: an amount on a project, a share on
   *  a template whose sum is not known yet. */
  feeLabelOf: (fee: T) => string,
): string | null {
  if (isPlanningFee(row)) {
    const target = rows.find(other => other.id === row.creditedInstallmentId)
    return target
      ? FINANCE.installments.creditedAgainst(target.title)
      : FINANCE.installments.notCredited
  }
  const fee = planningFees(rows).find(
    other => other.creditedInstallmentId === row.id,
  )
  return fee ? FINANCE.installments.reducedBy(feeLabelOf(fee)) : null
}

/** What a row's amount is computed from. */
export type AmountRow = Pick<
  FinanceInstallment,
  'id' | 'sortOrder' | 'role' | 'percent' | 'amount' | 'creditedInstallmentId'
>

/** One row's figure before any credit: its share of the sum, or its amount. */
const figureOf = (netAmount: number, installment: AmountRow) =>
  installment.percent != null
    ? percentOf(netAmount, installment.percent)
    : roundMoney(installment.amount ?? 0)

/**
 * What each installment of the plan comes to before the fee is credited.
 *
 * A percent installment is its share of the contract, rounded to the cent;
 * a fixed one is its amount. The last percent installment takes whatever
 * the rounding left over, so the plan sums to the contract exactly — which
 * is what the contract itself says happens with the cents.
 */
export function planAmounts(
  netAmount: number,
  installments: readonly AmountRow[],
): Map<number, number> {
  const ordered = planInstallments(installments)
  const amounts = new Map<number, number>()
  for (const installment of ordered) {
    amounts.set(installment.id, figureOf(netAmount, installment))
  }
  // Each percent installment is rounded on its own; the cents that rounding
  // loses or gains over all of them land on the last one, so a plan of
  // percents adds up to exactly its share. A plan whose percents do not make
  // 100 stays short or over, which is what `balanced` reports.
  const percents = ordered.filter(installment => installment.percent != null)
  const last = percents[percents.length - 1]
  if (last) {
    const share = percentOf(
      netAmount,
      percents.reduce((sum, installment) => sum + installment.percent!, 0),
    )
    const others = sumMoney(
      percents.slice(0, -1).map(installment => amounts.get(installment.id)!),
    )
    amounts.set(last.id, roundMoney(share - others))
  }
  return amounts
}

/**
 * What each row comes to, by id: the plan's installments, and the
 * Planungspauschale beside them, taken off the installment it is credited
 * against. A fee stated as a percent — a template's — is its share of the
 * sum.
 */
export function installmentAmounts(
  netAmount: number,
  installments: readonly AmountRow[],
): Map<number, number> {
  const amounts = planAmounts(netAmount, installments)
  for (const fee of planningFees(installments)) {
    const amount = figureOf(netAmount, fee)
    amounts.set(fee.id, amount)
    const target = fee.creditedInstallmentId
    if (target != null && amounts.has(target)) {
      // A fee larger than its installment is refused on the way in; the
      // floor keeps a plan edited around it from going negative.
      amounts.set(
        target,
        Math.max(0, roundMoney(amounts.get(target)! - amount)),
      )
    }
  }
  return amounts
}

/** What the plan's installments add up to: the contract's sum where the
 *  percents make 100 and the fixed amounts the rest. The Planungspauschale
 *  stands beside the plan and is not part of it. */
export function planSum(
  netAmount: number,
  installments: readonly AmountRow[],
): number {
  return sumMoney([...planAmounts(netAmount, installments).values()])
}

/** What the Planungspauschale adds to the contract: the part of it credited
 *  nowhere, which the customer pays on top of the sum. */
export function planningFeeOnTop(
  netAmount: number,
  installments: readonly AmountRow[],
): number {
  return sumMoney(
    planningFees(installments)
      .filter(fee => fee.creditedInstallmentId == null)
      .map(fee => figureOf(netAmount, fee)),
  )
}

/**
 * Whether an installment has fallen due: its milestone is reached, its date
 * has passed, or it is immediate on a contract that is in force.
 */
function installmentDue(
  installment: FinanceInstallment,
  contractStatus: ContractStatus,
  milestoneReached: (milestoneId: number) => boolean,
  today: Date = new Date(),
): boolean {
  if (contractStatus !== 'ACTIVE') return false
  switch (installment.dueKind) {
    case 'IMMEDIATE':
      return true
    case 'DATE':
      return (
        installment.dueDate != null && new Date(installment.dueDate) <= today
      )
    case 'MILESTONE':
      return (
        installment.milestoneId != null &&
        milestoneReached(installment.milestoneId)
      )
  }
}

// ── Approvals and deductions ────────────────────────────────────────────────

/** What one approval releases for payment: its amount less what it withheld. */
export const approvalPayable = (approval: FinanceApproval): number =>
  roundMoney(approval.amount - sumMoney(approval.deductions.map(d => d.amount)))

/** How much of each deduction has been taken so far, over every approval on
 *  the contract's invoices that count, by deduction id. */
function deductionsTakenSoFar(
  contract: Pick<FinanceContract, 'invoices'>,
): Map<number, number> {
  const taken = new Map<number, number>()
  for (const invoice of countingInvoices(contract.invoices)) {
    for (const allocation of invoice.allocations) {
      for (const approval of allocation.approvals) {
        for (const d of approval.deductions) {
          if (d.deductionId == null) continue
          taken.set(d.deductionId, (taken.get(d.deductionId) ?? 0) + d.amount)
        }
      }
    }
  }
  for (const [id, sum] of taken) taken.set(id, roundMoney(sum))
  return taken
}

/**
 * What a deduction typed on one approval takes: its amount, or its percent
 * of the approved amount, signed like the approval so a credit note's
 * deduction lessens the refund the way it lessened the payment.
 */
export function manualDeductionAmount(
  approved: number,
  extra: { amount?: number | null; percent?: number | null },
): number {
  if (extra.amount != null) {
    return roundMoney(signOf(approved) * Math.abs(extra.amount))
  }
  return percentOf(approved, extra.percent ?? 0)
}

/** What a contract-wide deduction withholds over the whole contract. */
export const deductionTotal = (
  contract: Pick<FinanceContract, 'netAmount'>,
  deduction: Pick<FinanceDeduction, 'percent' | 'amount'>,
): number =>
  deduction.amount ?? percentOf(contract.netAmount, deduction.percent ?? 0)

/**
 * The deductions an approval of `amount` on `allocation` takes by itself.
 *
 * A per-invoice deduction is its percent of the amount. A contract-wide one is
 * its percent of the contract sum, or its fixed sum, in total: taken from the
 * first approvals until it is exhausted, or only from the lines that invoice
 * the installment it names. A line invoicing the retention back takes none:
 * the money it releases was already withheld once.
 */
export const plannedDeductions = (
  contract: FinanceContract,
  allocation: Pick<
    FinanceAllocation,
    'id' | 'installmentId' | 'retentionRelease'
  >,
  amount: number,
): PlannedDeduction[] => {
  const walk = walkChecksToCome(contract, allocation.id)
  return planAgainst(contract, allocation, amount, walk.taken, walk.withheld)
}

/** The deductions on `amount` of `allocation`, given what each deduction
 *  has taken or is promised before it, and what of that an approval has
 *  withheld already. */
function planAgainst(
  contract: FinanceContract,
  allocation: Pick<FinanceAllocation, 'installmentId' | 'retentionRelease'>,
  amount: number,
  takenById: ReadonlyMap<number, number>,
  withheldById: ReadonlyMap<number, number>,
): PlannedDeduction[] {
  if (allocation.retentionRelease) return []
  return contract.deductions.flatMap(deduction => {
    // A deduction taken at one installment waits for a line on it.
    if (
      deduction.deductAtInstallmentId != null &&
      deduction.deductAtInstallmentId !== allocation.installmentId
    ) {
      return []
    }
    const takenSoFar = takenById.get(deduction.id) ?? 0
    // Signed like the amount: a credit note gives back what was withheld
    // from what it takes back, never more than an approval has withheld in
    // all — a promise to a check still to come is nothing to give back.
    // A positive approval never gives anything back, whatever the sum of
    // earlier credits has done to what was taken so far.
    const floor = amount < 0 ? -(withheldById.get(deduction.id) ?? 0) : 0
    const taken =
      deduction.basis === 'PER_INVOICE'
        ? Math.max(percentOf(amount, deduction.percent ?? 0), floor)
        : clamp(
            amount,
            floor,
            // A sum lowered below what was withheld leaves nothing to take;
            // it does not hand the surplus back through an approval.
            Math.max(
              0,
              roundMoney(deductionTotal(contract, deduction) - takenSoFar),
            ),
          )
    if (taken === 0) return []
    return [
      {
        deductionId: deduction.id,
        title: deduction.title,
        percent: deduction.percent,
        amount: taken,
        settlement: deduction.settlement,
      },
    ]
  })
}

/**
 * The checks still to come, walked in the order the invoices and their
 * lines stand: every line's unapproved part takes what the deductions
 * leave it after what was withheld and what the lines before it are
 * promised. One walk serves the forecast and the approval, so a
 * contract-wide deduction is promised once and taken as promised, whatever
 * order the checks happen in. `until` stops before that line.
 */
function walkChecksToCome(contract: FinanceContract, until?: number) {
  // Two sums per deduction: what is withheld in all, lowered by the credit
  // notes to come, is what a credit note may still give back; what is taken
  // in all, raised by the checks to come, is what is left for a check to
  // take. A promise to give back opens no room, and a promise to take
  // leaves nothing to return.
  const withheld = deductionsTakenSoFar(contract)
  const taken = new Map(withheld)
  const expected = new Map<number, PlannedDeduction[]>()
  for (const invoice of orderedInvoices(contract.invoices)) {
    for (const allocation of orderedLines(invoice)) {
      if (allocation.id === until) return { expected, taken, withheld }
      const unapproved = roundMoney(
        allocation.amount - sumMoney(allocation.approvals.map(a => a.amount)),
      )
      if (sameMoney(unapproved, 0)) continue
      const planned = planAgainst(
        contract,
        allocation,
        unapproved,
        taken,
        withheld,
      )
      for (const d of planned) {
        const sum = d.amount < 0 ? withheld : taken
        sum.set(
          d.deductionId,
          roundMoney((sum.get(d.deductionId) ?? 0) + d.amount),
        )
      }
      expected.set(allocation.id, planned)
    }
  }
  return { expected, taken, withheld }
}

/** What the checks still to come would take off each line, by allocation
 *  id. */
export const expectedDeductionsOf = (
  contract: FinanceContract,
): Map<number, PlannedDeduction[]> => walkChecksToCome(contract).expected

/** What the checks still to come would take off one line. */
const expectedOn = (
  expected: ReadonlyMap<number, PlannedDeduction[]> | undefined,
  allocationId: number,
): number => sumMoney((expected?.get(allocationId) ?? []).map(d => d.amount))

// ── Invoices ────────────────────────────────────────────────────────────────

/**
 * An invoice and the corrections to it that count: what a payment against
 * the invoice settles as one sum. A correction is its own group of one.
 */
export const correctionGroupOf = <
  T extends {
    id: number
    status: InvoiceStatus
    correctsInvoiceId: number | null
  },
>(
  invoices: readonly T[],
  invoice: T,
): T[] =>
  invoice.correctsInvoiceId != null
    ? [invoice]
    : [
        invoice,
        ...countingInvoices(invoices).filter(
          i => i.correctsInvoiceId === invoice.id,
        ),
      ]

/**
 * What one payment against an invoice settles: the invoice and its
 * corrections that count, each with what it has open, their sum, and the
 * way the money moves — the sign of what is open, so a paid invoice a
 * correction lowered is owed back; with nothing open, the way the group was
 * invoiced.
 */
export function paymentGroupOf<
  T extends {
    id: number
    status: InvoiceStatus
    correctsInvoiceId: number | null
    netAmount: number
  },
>(invoices: readonly T[], invoice: T, openOf: (member: T) => number) {
  const group = correctionGroupOf(invoices, invoice)
  const members = group.map(member => ({ id: member.id, open: openOf(member) }))
  const open = sumMoney(members.map(m => m.open))
  const sign = sameMoney(open, 0)
    ? signOf(sumMoney(group.map(member => member.netAmount)))
    : signOf(open)
  return { members, open, sign }
}

/** A fixed sum is a total over the contract, whatever basis was asked. */
export const deductionBasisOf = (
  amount: number | null | undefined,
  basis: DeductionBasis,
): DeductionBasis => (amount != null ? 'OF_CONTRACT' : basis)

/**
 * One payment over the open figures of a group, as one allocation per
 * member: a member whose open figure points the other way — a correction
 * that lowers the sum — is settled whole first, and what that leaves goes to
 * the others in order, each up to what it has open. The caller has checked
 * the amount against the group's sum.
 */
export function splitPayment(
  members: readonly { id: number; open: number }[],
  amount: number,
): { id: number; amount: number }[] {
  const sign = signOf(amount)
  const against = members.filter(m => pointsAgainst(m.open, sign))
  const along = members.filter(m => !pointsAgainst(m.open, sign))
  const parts = against.map(m => ({ id: m.id, amount: m.open }))
  let rest = roundMoney(amount - sumMoney(against.map(m => m.open)))
  for (const m of along) {
    const take = roundMoney(sign * Math.min(Math.abs(m.open), Math.abs(rest)))
    parts.push({ id: m.id, amount: take })
    rest = roundMoney(rest - take)
  }
  // What a payment within the rounding tolerance pays beyond what is open
  // goes to the last that takes money this way, else the first settled whole.
  const last = along.length > 0 ? parts.at(-1) : parts[0]
  if (last && !sameMoney(rest, 0)) last.amount = roundMoney(last.amount + rest)
  return parts.filter(part => !sameMoney(part.amount, 0))
}

/** The invoices that count: a cancelled one counts nowhere. */
export const countingInvoices = <T extends Pick<FinanceInvoice, 'status'>>(
  invoices: readonly T[],
) => invoices.filter(invoice => invoice.status !== 'CANCELLED')

/** The invoices that count in the order the checks walk them: by date,
 *  then by id. */
const orderedInvoices = (invoices: readonly FinanceInvoice[]) =>
  countingInvoices(invoices).sort(
    (a, b) =>
      new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime() ||
      a.id - b.id,
  )

/** An invoice's lines in the order they stand on it. */
const orderedLines = (invoice: Pick<FinanceInvoice, 'allocations'>) =>
  [...invoice.allocations].sort((a, b) => a.sortOrder - b.sortOrder)

/**
 * What was paid on an invoice, attributed to its lines in their order: the
 * first line's payable is covered first, then the next. Payments are made
 * against the invoice, so this is the only way a line can say it is paid.
 */
export function allocationFigures(
  invoice: Pick<FinanceInvoice, 'allocations' | 'payments'>,
  /** From `expectedDeductionsOf`; without it nothing is expected. */
  expected?: ReadonlyMap<number, PlannedDeduction[]>,
): Map<number, AllocationFigures> {
  const ordered = orderedLines(invoice)
  let paidLeft = sumMoney(invoice.payments.map(p => p.amount))
  const figures = new Map<number, AllocationFigures>()
  for (const allocation of ordered) {
    const approved = sumMoney(allocation.approvals.map(a => a.amount))
    const payable = sumMoney(allocation.approvals.map(approvalPayable))
    // A credit note's figures are negative: attribution runs the same way,
    // mirrored.
    const sign = signOf(payable)
    const paid =
      sign * roundMoney(Math.max(0, Math.min(sign * payable, sign * paidLeft)))
    paidLeft = roundMoney(paidLeft - paid)
    const expectedHere = expectedOn(expected, allocation.id)
    figures.set(allocation.id, {
      approved,
      unapproved: roundMoney(allocation.amount - approved),
      deducted: roundMoney(approved - payable),
      expected: expectedHere,
      withheld: roundMoney(approved - payable + expectedHere),
      payable,
      paid,
    })
  }
  return figures
}

const deductedBySettlement = (
  invoice: Pick<FinanceInvoice, 'allocations'>,
  settlement: DeductionSettlement,
): number =>
  sumMoney(
    invoice.allocations.flatMap(allocation =>
      allocation.approvals.flatMap(approval =>
        approval.deductions
          .filter(d => d.settlement === settlement)
          .map(d => d.amount),
      ),
    ),
  )

const ZERO_INVOICE: Omit<InvoiceFigures, 'issued' | 'gross'> = {
  allocated: 0,
  unallocated: 0,
  approved: 0,
  unapproved: 0,
  retained: 0,
  reduced: 0,
  deducted: 0,
  expected: 0,
  withheld: 0,
  payable: 0,
  paid: 0,
  open: 0,
  rounding: 0,
  owed: 0,
  overdue: false,
  state: 'CANCELLED',
}

export function invoiceFigures(
  invoice: FinanceInvoice,
  today: Date = new Date(),
  /** From `expectedDeductionsOf`; without it nothing is expected. */
  expected?: ReadonlyMap<number, PlannedDeduction[]>,
): InvoiceFigures {
  const issued = roundMoney(invoice.netAmount)
  const gross = grossOf(issued, invoice.vatRate)
  if (invoice.status === 'CANCELLED') {
    return { issued, gross, ...ZERO_INVOICE }
  }
  const allocated = sumMoney(invoice.allocations.map(a => a.amount))
  const approved = sumMoney(
    invoice.allocations.flatMap(a => a.approvals.map(ap => ap.amount)),
  )
  const retained = deductedBySettlement(invoice, 'RELEASED')
  const reduced = deductedBySettlement(invoice, 'NEVER_PAID')
  const payable = roundMoney(approved - retained - reduced)
  const paid = sumMoney(invoice.payments.map(p => p.amount))
  // A payment within the rounding tolerance of what was released settles
  // it; the cents it missed by are the rounding difference, not still open.
  const rest = roundMoney(payable - paid)
  const rounding = settles(paid, payable) ? rest : 0
  const open = roundMoney(rest - rounding)
  const expectedSum = sumMoney(
    invoice.allocations.map(a => expectedOn(expected, a.id)),
  )
  const owed = roundMoney(
    issued - retained - reduced - expectedSum - paid - rounding,
  )
  const overdue = isOverdue(owed, invoice.dueDate, today)
  const unapproved = roundMoney(allocated - approved)
  return {
    issued,
    gross,
    allocated,
    unallocated: roundMoney(issued - allocated),
    approved,
    unapproved,
    retained,
    reduced,
    deducted: roundMoney(retained + reduced),
    expected: expectedSum,
    withheld: roundMoney(retained + reduced + expectedSum),
    payable,
    paid,
    open,
    rounding,
    owed,
    // What the other side owes is late, approved by this side or not: the
    // term runs on the invoice.
    overdue,
    state: invoiceState({ unapproved, overdue, open, paid }),
  }
}

/** The one state of an invoice: what is unchecked comes first, then what
 *  is late; nothing open with something paid is paid, nothing open because
 *  the deductions took everything is merely released. */
function invoiceState(f: {
  unapproved: number
  overdue: boolean
  open: number
  paid: number
}): InvoiceState {
  if (!sameMoney(f.unapproved, 0)) return 'REVIEW'
  if (f.overdue) return 'OVERDUE'
  if (sameMoney(f.open, 0) && !sameMoney(f.paid, 0)) return 'PAID'
  return 'RELEASED'
}

/** A day in milliseconds; a due date is a calendar day at UTC midnight. */
const DAY_MS = 86_400_000

/** Whether something still owed on an invoice has passed its due date: the
 *  due day itself is still in time, the day after is late. */
export const isOverdue = (
  owed: number,
  dueDate: Date | string | null,
  today: Date,
): boolean =>
  owed > 0 &&
  dueDate != null &&
  today.getTime() >= new Date(dueDate).getTime() + DAY_MS

/** How many days something overdue has been late, by the same day rule;
 *  never less than one. */
export const daysOverdue = (dueDate: Date | string, today: Date): number =>
  Math.max(
    1,
    Math.floor((today.getTime() - new Date(dueDate).getTime()) / DAY_MS),
  )

/** The due date an invoice gets where none was typed: the invoice date plus
 *  the contract's term, or none where the contract has no term. */
export function defaultDueDate(
  invoiceDate: Date,
  paymentTermDays: number | null,
): Date | null {
  if (paymentTermDays == null) return null
  // Calendar days on a date-only value: UTC has no clock change, so a day is
  // always 24 hours and neither the server's nor the reader's zone shifts it.
  return new Date(invoiceDate.getTime() + paymentTermDays * 86_400_000)
}

// ── Installments, as the invoices have got them ─────────────────────────────

/** The installments a line of an invoice that counts names, which then
 *  cannot be removed. A cancelled invoice counts nowhere. */
const allocatedInstallmentIds = (
  contract: Pick<FinanceContract, 'invoices'>,
): Set<number | null> =>
  new Set(
    countingInvoices(contract.invoices).flatMap(invoice =>
      invoice.allocations.map(a => a.installmentId),
    ),
  )

/** Whether a line of an invoice that counts names the installment. */
export const installmentAllocated = (
  contract: Pick<FinanceContract, 'invoices'>,
  installmentId: number,
): boolean => allocatedInstallmentIds(contract).has(installmentId)

/** The percent installments an invoice names: a changed sum turns them into
 *  the amount they had. */
export const allocatedPercentInstallments = (
  contract: Pick<FinanceContract, 'installments' | 'invoices'>,
): FinanceInstallment[] => {
  const allocated = allocatedInstallmentIds(contract)
  return contract.installments.filter(
    i => i.percent != null && allocated.has(i.id),
  )
}

/** A line as the installment checks read it. */
type BillingLine = { installmentId?: number | null; amount: number }

/** What the installment checks read of a contract. */
type BillableContract = {
  netAmount: number
  installments: readonly AmountRow[]
  invoices: readonly {
    id: number
    status: InvoiceStatus
    allocations: readonly BillingLine[]
  }[]
}

/**
 * What the lines bill on each installment they name, beside what it had
 * left before them: its planned amount less what the other invoices that
 * count put on it. Only installments a line bills a positive amount on are
 * measured; a credit note's never are. `invoiceId` is the invoice the lines
 * belong to, left out of what was put on the installment so far.
 */
function installmentBilling(
  contract: BillableContract,
  lines: readonly BillingLine[],
  invoiceId?: number,
): { left: number; billed: number }[] {
  const amounts = installmentAmounts(contract.netAmount, contract.installments)
  const others = countingInvoices(contract.invoices)
    .filter(invoice => invoice.id !== invoiceId)
    .flatMap(invoice => invoice.allocations)
  const ids = new Set(
    lines
      .filter(line => line.installmentId != null && line.amount > 0)
      .map(line => line.installmentId!),
  )
  const onInstallment = (id: number, rows: readonly BillingLine[]) =>
    sumMoney(rows.filter(r => r.installmentId === id).map(r => r.amount))
  return [...ids].map(id => ({
    left: roundMoney((amounts.get(id) ?? 0) - onInstallment(id, others)),
    billed: onInstallment(id, lines),
  }))
}

const isOverbilled = ({ left, billed }: { left: number; billed: number }) =>
  exceeds(billed, left, 1)

/** Whether the lines bill an installment beyond what it has left. */
export const overbillsInstallment = (
  contract: BillableContract,
  lines: readonly BillingLine[],
): boolean => installmentBilling(contract, lines).some(isOverbilled)

/** Why an invoice cannot be approved in full as it is recorded: its lines
 *  on an installment bill less than the installment has left, which wants a
 *  check that says why; or they bill more than it allows. */
export type ApproveOnCreateRefusal = 'LINE_CUT' | 'OVER_INSTALLMENT'

export function approveOnCreateRefusal(
  contract: BillableContract,
  lines: readonly BillingLine[],
  invoiceId?: number,
): ApproveOnCreateRefusal | null {
  const billing = installmentBilling(contract, lines, invoiceId)
  if (billing.some(isOverbilled)) return 'OVER_INSTALLMENT'
  return billing.some(({ left, billed }) => exceeds(left, billed, 1))
    ? 'LINE_CUT'
    : null
}

export function installmentFigures(
  contract: FinanceContract,
  milestoneReached: (milestoneId: number) => boolean,
  today: Date = new Date(),
): Map<number, InstallmentFigures> {
  const amounts = installmentAmounts(contract.netAmount, contract.installments)
  const figures = new Map<number, InstallmentFigures>()
  const allocated = allocatedInstallmentIds(contract)
  const counting = countingInvoices(contract.invoices).map(invoice => ({
    invoice,
    byLine: allocationFigures(invoice),
  }))

  for (const installment of contract.installments) {
    let invoiced = 0
    let approved = 0
    let withheld = 0
    let payable = 0
    let paid = 0
    for (const { invoice, byLine } of counting) {
      for (const line of invoice.allocations) {
        if (line.installmentId !== installment.id) continue
        const f = byLine.get(line.id)!
        invoiced += line.amount
        approved += f.approved
        withheld += f.withheld
        payable += f.payable
        paid += f.paid
      }
    }
    const planned = amounts.get(installment.id) ?? 0
    const due = installmentDue(
      installment,
      contract.status,
      milestoneReached,
      today,
    )
    figures.set(installment.id, {
      planned,
      due,
      invoiced: roundMoney(invoiced),
      approved: roundMoney(approved),
      withheld: roundMoney(withheld),
      payable: roundMoney(payable),
      paid: roundMoney(paid),
      state: installmentState({
        planned,
        due,
        invoiced,
        approved,
        paid,
        payable,
      }),
      allocated: allocated.has(installment.id),
    })
  }
  return figures
}

function installmentState(f: {
  planned: number
  due: boolean
  invoiced: number
  approved: number
  paid: number
  payable: number
}): InstallmentState {
  const fullyInvoiced = f.invoiced > 0 && !exceeds(f.planned, f.invoiced, 1)
  // Settled: everything invoiced on it is approved and what that leaves to
  // pay is paid — which may be nothing, where a security took all of it.
  if (
    fullyInvoiced &&
    sameMoney(f.approved, f.invoiced) &&
    settles(f.paid, f.payable)
  ) {
    return 'PAID'
  }
  if (f.approved > 0) return 'APPROVED'
  if (f.invoiced > 0) return 'INVOICED'
  return f.due ? 'DUE' : 'PLANNED'
}

// ── Contracts ───────────────────────────────────────────────────────────────

export function contractFigures(
  contract: FinanceContract,
  today: Date = new Date(),
  expected: ReadonlyMap<number, PlannedDeduction[]> = expectedDeductionsOf(
    contract,
  ),
): ContractFigures {
  const counting = countingInvoices(contract.invoices)
  const invoices = counting.map(invoice =>
    invoiceFigures(invoice, today, expected),
  )
  const total = (key: Exclude<keyof InvoiceFigures, 'overdue' | 'state'>) =>
    sumMoney(invoices.map(f => f[key]))
  const issued = total('issued')
  const retained = total('retained')
  const retentionLines = counting.flatMap(invoice =>
    invoice.allocations.filter(a => a.retentionRelease),
  )
  // The security invoiced back is money withheld earlier, not work: it is
  // part of what was issued, not of what counts against the contract.
  const invoiced = roundMoney(
    issued - sumMoney(retentionLines.map(a => a.amount)),
  )
  // What of the security has come back: paid against the lines that
  // invoice it, not merely approved there.
  const retentionReleased = sumMoney(
    counting.flatMap(invoice => {
      const byLine = allocationFigures(invoice)
      return invoice.allocations
        .filter(a => a.retentionRelease)
        .map(a => byLine.get(a.id)!.paid)
    }),
  )
  const plan = planSum(contract.netAmount, contract.installments)
  // What the contract comes to in all: its sum, plus a Planungspauschale
  // charged beside it; a credited one is inside the sum already.
  const sum = isDirect(contract) ? invoiced : contract.netAmount
  const owedInAll = roundMoney(
    sum + planningFeeOnTop(contract.netAmount, contract.installments),
  )
  return {
    sum,
    planSum: plan,
    balanced:
      planInstallments(contract.installments).length === 0 ||
      sameMoney(plan, contract.netAmount),
    issued,
    invoiced,
    remaining: roundMoney(owedInAll - invoiced),
    approved: total('approved'),
    unapproved: total('unapproved'),
    retained,
    retentionReleased,
    retentionHeld: roundMoney(retained - retentionReleased),
    reduced: total('reduced'),
    payable: total('payable'),
    paid: total('paid'),
    open: total('open'),
    owed: total('owed'),
    overdueCount: invoices.filter(f => f.overdue).length,
  }
}

// ── The project ─────────────────────────────────────────────────────────────

const EMPTY_SIDE: SideFigures = {
  contracted: 0,
  issued: 0,
  approved: 0,
  payable: 0,
  paid: 0,
  open: 0,
  retentionHeld: 0,
  overdueCount: 0,
}

/** A contract with its figures, as the project sums them. */
export type FiguredContract = Pick<
  FinanceContract,
  'status' | 'direction' | 'kind'
> & { figures: ContractFigures }

/**
 * What a contract adds to its side's contracted sum: its sum. A DIRECT
 * payable one's is what its invoices come to, which the project owes as
 * surely as a contract sum; a DIRECT receivable one adds nothing, since the
 * Auftragssumme is what the customer signed.
 */
const contractedOf = (contract: FiguredContract): number =>
  isDirect(contract) && contract.direction === 'RECEIVABLE'
    ? 0
    : contract.figures.sum

function sideFigures(contracts: readonly FiguredContract[]): SideFigures {
  return contracts.reduce<SideFigures>((side, contract) => {
    const { figures } = contract
    return {
      contracted: roundMoney(side.contracted + contractedOf(contract)),
      issued: roundMoney(side.issued + figures.issued),
      approved: roundMoney(side.approved + figures.approved),
      payable: roundMoney(side.payable + figures.payable),
      paid: roundMoney(side.paid + figures.paid),
      open: roundMoney(side.open + figures.open),
      retentionHeld: roundMoney(side.retentionHeld + figures.retentionHeld),
      overdueCount: side.overdueCount + figures.overdueCount,
    }
  }, EMPTY_SIDE)
}

export function projectFigures(
  contracts: readonly FiguredContract[],
): ProjectFigures {
  const active = contracts.filter(c => c.status === 'ACTIVE')
  const receivable = active.filter(c => c.direction === 'RECEIVABLE')
  const payable = active.filter(c => c.direction === 'PAYABLE')
  const r = sideFigures(receivable)
  const p = sideFigures(payable)
  // What the payable side will come to once everything is settled: the
  // larger of each contract and what was invoiced on it, less what is never
  // paid. The receivable side is what the customer signed.
  const expectedPayable = sumMoney(
    payable.map(contract =>
      roundMoney(
        Math.max(contract.figures.sum, contract.figures.invoiced) -
          contract.figures.reduced,
      ),
    ),
  )
  // Invoices to the customer outside the Auftragssumme are earned all the
  // same, so both margins count them: a DIRECT contract's sum is its invoices.
  const earned = sumMoney(receivable.map(contract => contract.figures.sum))
  return {
    receivable: r,
    payable: p,
    earned,
    marginContracted: roundMoney(earned - p.contracted),
    marginForecast: roundMoney(earned - expectedPayable),
    marginCash: roundMoney(r.paid - p.paid),
  }
}
